import React, { useCallback, useEffect } from 'react';
import { DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useIntl } from '@olxpwa-lib/i18n/client';
import { useDatePicker } from '~hooks';
import { RenderCalendarDay } from '~components/AdServiceBooking/RenderCalendarDay';
import { TestId } from '~testId';
import { languageData } from '~types';
import { format, addDays } from 'date-fns';
import { useAppContext } from '~state/App.context';
import { BookingCalendarFormValue } from '~components/AdServiceBooking/types';
import {
    ClearDates,
    StyledDayPicker,
    Wrapper,
    formatWeekdayName,
    modifiersStyles
} from './DatePicker.components';
import { bookingSummaryKeys } from '~tracking';
import { usePolymorphicLayoutEffect } from '@olxpwa-lib/utils/hooks';

export type DatePickerProps = {
    isStr: boolean;
    showAvailability: boolean;
    displayBlockDatesFeature?: boolean;
    isSelectedDateInBlockedDates?: boolean;
    setDatePickerValues?: (value: BookingCalendarFormValue) => void;
    defaultMonth?: Date;
    defaultSelected?: DateRange;
    bookedDates?: Date[];
    modifiers?: {
        checkoutDates?: Date[];
        checkinDates?: Date[];
        spCheckoutDates?: Date[];
        spCheckinDates?: Date[];
        blockedDates?: Date[];
        availabilityCheckins?: Date[];
        availabilityCheckouts?: Date[];
    };
    isSelectable?: boolean;
    bookingSummary?: boolean;
    minimumStayRequirement?: number;
};

export const minimumStayRequirementDates = (
    dateFrom: Date | undefined,
    minStay: number
) => {
    if (!dateFrom) return [];
    return Array.from({ length: minStay }, (_, i) => addDays(dateFrom, i));
};

export const DatePicker = ({
    defaultMonth,
    showAvailability,
    defaultSelected,
    bookedDates = [],
    modifiers,
    isSelectable = true,
    displayBlockDatesFeature = false,
    isSelectedDateInBlockedDates = false,
    isStr,
    clearButtonDisabled = true,
    setDatePickerValues,
    bookingSummary = false,
    minimumStayRequirement = 1
}: DatePickerProps & { clearButtonDisabled: boolean }) => {
    const { appConfig, tracker } = useAppContext();
    const { locale } = appConfig;
    const { formatMessage } = useIntl();

    usePolymorphicLayoutEffect(() => {
        tracker.setTouchPointPage(
            bookingSummary ? bookingSummaryKeys.touch_point_button : null
        );
    }, []);

    const {
        checkinDates,
        checkoutDates,
        blockedDates,
        todayDate,
        disabledDates,
        currentMonth,
        selectedRange,
        maxDaySelection,
        dateFrom,
        dateTo,
        handleDateSelect,
        handleResetClick
    } = useDatePicker({
        bookedDates,
        showAvailability,
        defaultSelected,
        modifiers,
        defaultMonth,
        isStr,
        setDatePickerValues
    });

    const modifiedDates = useCallback(
        () => ({
            checkOutDates: displayBlockDatesFeature
                ? []
                : checkinDates?.map((date: Date) => new Date(date)) || [],
            spCheckoutDates: displayBlockDatesFeature
                ? checkinDates?.map((date: Date) => new Date(date)) || []
                : [],
            checkInDates:
                checkoutDates?.map((date: Date) => new Date(date)) || [],
            blockedDates: displayBlockDatesFeature
                ? blockedDates?.map((date: Date) => new Date(date)) || []
                : [],
            bookedDates: displayBlockDatesFeature ? [] : bookedDates,
            today: todayDate.current,
            dateFrom: displayBlockDatesFeature ? new Date(dateFrom ?? '') : [],
            dateTo: displayBlockDatesFeature ? new Date(dateTo ?? '') : [],
            availabilityCheckins:
                displayBlockDatesFeature &&
                modifiers &&
                modifiers.availabilityCheckins
                    ? modifiers.availabilityCheckins.map(date => new Date(date))
                    : [],
            availabilityCheckouts:
                displayBlockDatesFeature &&
                modifiers &&
                modifiers.availabilityCheckouts
                    ? modifiers.availabilityCheckouts.map(
                          date => new Date(date)
                      )
                    : [],
            checkoutsWithBlockedDates: displayBlockDatesFeature
                ? blockedDates
                      ?.map(date => new Date(date))
                      .filter(date =>
                          modifiers?.availabilityCheckouts
                              ?.map(date => new Date(date))
                              .toString()
                              .includes(date.toString())
                      ) || []
                : [],
            checkinsWithBlockedDates: displayBlockDatesFeature
                ? blockedDates
                      ?.map(date => new Date(date))
                      .filter(date =>
                          modifiers?.availabilityCheckins
                              ?.map(date => new Date(date))
                              .toString()
                              .includes(date.toString())
                      ) || []
                : [],
            minimumStayRequirementDates: minimumStayRequirementDates(
                dateFrom,
                minimumStayRequirement
            )
        }),
        [
            blockedDates,
            bookedDates,
            checkinDates,
            checkoutDates,
            dateFrom,
            dateTo,
            displayBlockDatesFeature,
            modifiers,
            todayDate,
            minimumStayRequirement
        ]
    );

    const isDateFromInCheckins = useCallback(() => {
        const value = checkinDates
            ?.map(date => format(new Date(date), 'iii MMM dd yyyy'))
            .filter(date =>
                dateFrom
                    ?.toString()
                    .includes(format(new Date(date), 'iii MMM dd yyyy'))
            );
        if (!!value) {
            return value.length > 0;
        }
        return false;
    }, [checkinDates, dateFrom]);

    useEffect(() => {
        setDatePickerValues?.({ dateFrom, dateTo });
    }, [dateFrom, dateTo, setDatePickerValues]);

    return (
        <>
            <Wrapper data-testid={TestId.bookingCalendar}>
                <StyledDayPicker
                    mode="range"
                    disabled={[
                        disabledDates,
                        ...bookedDates,
                        ...minimumStayRequirementDates(
                            dateFrom,
                            minimumStayRequirement
                        ).slice(1)
                    ]}
                    defaultMonth={currentMonth}
                    fromMonth={currentMonth}
                    selected={selectedRange}
                    onSelect={isSelectable ? handleDateSelect : () => {}}
                    locale={languageData[locale]}
                    formatters={{ formatWeekdayName }}
                    components={{
                        Day: props =>
                            RenderCalendarDay(
                                { props, minimumStayRequirement },
                                displayBlockDatesFeature
                            )
                    }}
                    modifiers={modifiedDates()}
                    modifiersStyles={modifiersStyles(
                        isSelectedDateInBlockedDates,
                        isStr
                    )}
                    max={maxDaySelection}
                    modifiersClassNames={{
                        checkoutsWithBlockedDates:
                            'booking-checkouts-with-blocked-dates',
                        checkinsWithBlockedDates:
                            'booking-checkins-with-blocked-dates',
                        availabilityCheckouts: 'booking-availability-checkouts',
                        availabilityCheckins: 'booking-availability-checkins',
                        checkoutWithDateFrom: 'booking-checkout-with-date-from',
                        checkinWithDateTo: 'booking-checkin-with-date-to',
                        spCheckoutDates: 'booking-checkout-date',
                        checkInDates: 'booking-checkin-date',
                        blockedDates: 'booking-blocked-dates',
                        bookedDates: 'booking-booked-date',
                        dateFrom: 'booking-date-from',
                        dateTo: 'booking-date-to',
                        today: 'booking-today-date'
                    }}
                    displayBlockDatesFeature={displayBlockDatesFeature}
                    isSelectedDateInBlockedDates={isSelectedDateInBlockedDates}
                    isStr={isStr}
                    dateFrom={!!dateFrom}
                    isDateFromInCheckins={isDateFromInCheckins()}
                />
                {!clearButtonDisabled && (
                    <ClearDates
                        variant="tertiary"
                        onClick={handleResetClick}
                        data-testid={TestId.bookingCalendarClearButton}
                    >
                        {formatMessage({
                            id: 'service.booking.calendar.clear.dates',
                            defaultMessage: 'Clear Dates'
                        })}
                    </ClearDates>
                )}
            </Wrapper>
        </>
    );
};
